// src/components/virtualization/listBoxComponent.js
import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useLayoutEffect,
  useMemo,
} from "react";
import { VariableSizeList as List } from "react-window";
import { styled } from "@mui/material/styles";

import { useWindowResize } from "./useWindowResize";
import AutoSizer from "react-virtualized-auto-sizer";

const StyledListItem = styled("li")({
  // wordBreak: "break-word",
  // whiteSpace: "normal",
  // padding: "8px",
});

function useMemoizedChildrenByKeys(children) {
  const arrayChildren = useMemo(
    () => React.Children.toArray(children),
    [children]
  );

  const signature = useMemo(() => {
    const parts = arrayChildren.map((c) => {
      const k = c?.key ?? "";
      const v =
        c?.props?.dataSig ??
        c?.props?.label ??
        c?.props?.value ??
        (typeof c?.props?.children === "string" ? c.props.children.length : "");
      return `${k}:${v}`;
    });
    return `${parts.join("|")}#${arrayChildren.length}`;
  }, [arrayChildren]);

  const [memoChildren, setMemoChildren] = useState(arrayChildren);
  const prevSigRef = useRef(signature);

  useEffect(() => {
    if (signature !== prevSigRef.current) {
      setMemoChildren(arrayChildren);
      prevSigRef.current = signature;
    }
  }, [signature, arrayChildren]);

  return { memoChildren, signature };
}

const Row = ({ data, index, setSize, windowWidth, style }) => {
  const rowRef = useRef();
  const { ref, ...rest } = data[index];

  useLayoutEffect(() => {
    if (rowRef.current) {
      const height = rowRef.current.getBoundingClientRect().height;
      setSize(index, height);
    }
  }, [setSize, index, windowWidth, rest.key]);

  return (
    <StyledListItem
      ref={rowRef}
      {...rest.props}
      style={{
        ...style,
        height: "auto",
        top: style.top,
      }}
    >
      {/* Usa o conteudo do renderOption quando houver; senao o texto da key */}
      {rest.props?.children ?? (rest.key ? String(rest.key).slice(4) : null)}
    </StyledListItem>
  );
};

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const ListboxComponent = React.forwardRef(function ListboxComponent(
  props,
  ref
) {
  const { children, ...other } = props;
  const listRef = useRef();
  const [totalHeight, setTotalHeight] = useState(100);
  const [isInitialMeasure, setIsInitialMeasure] = useState(true);
  const { memoChildren, signature: childrenSig } =
    useMemoizedChildrenByKeys(children);

  const itemData = React.useMemo(
    () =>
      React.Children.toArray(memoChildren).map((child) => ({
        ...child,
        ref: React.createRef(),
      })),
    [memoChildren]
  );

  const itemCount = itemData.length;
  const sizeMap = useRef({});
  const measuredCount = useRef(0);

  const setSize = useCallback(
    (index, size) => {
      if (sizeMap.current[index] !== size) {
        sizeMap.current[index] = size;

        if (isInitialMeasure && !sizeMap.current[`measured_${index}`]) {
          sizeMap.current[`measured_${index}`] = true;
          measuredCount.current += 1;
        }

        const calculatedTotal = Object.keys(sizeMap.current)
          .filter((key) => !key.startsWith("measured_"))
          .reduce((acc, key) => acc + (sizeMap.current[key] || 0), 0);

        setTotalHeight(calculatedTotal);

        if (listRef.current) {
          // Recalcular tudo para refletir novas alturas
          listRef.current.resetAfterIndex(0, true);
        }

        if (measuredCount.current >= itemCount && isInitialMeasure) {
          setIsInitialMeasure(false);
        }
      }
    },
    [itemCount, isInitialMeasure]
  );

  const getSize = (index) => {
    return sizeMap.current[index] || 80;
  };

  const [windowWidth] = useWindowResize();

  useEffect(() => {
    sizeMap.current = {};
    measuredCount.current = 0;
    setIsInitialMeasure(true);
    setTotalHeight(100);
  }, [itemCount, childrenSig]);

  useEffect(() => {
    let id1, id2;
    id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => {
        if (listRef.current) {
          listRef.current.resetAfterIndex(0, true);
        }
      });
    });
    return () => {
      if (id1) cancelAnimationFrame(id1);
      if (id2) cancelAnimationFrame(id2);
    };
  }, [itemCount, childrenSig, windowWidth]);
  useEffect(() => {
    let id1, id2;
    id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => {
        if (listRef.current) {
          listRef.current.resetAfterIndex(0, true);
        }
      });
    });
    return () => {
      if (id1) cancelAnimationFrame(id1);
      if (id2) cancelAnimationFrame(id2);
    };
  }, [itemCount, childrenSig, windowWidth]);

  const containerHeight = Math.min(Math.max(totalHeight, 100), 340);

  return (
    <div
      ref={ref}
      style={{
        height: containerHeight ? `${containerHeight}px` : "300px",
        maxHeight: "300px",
        width: "100%",
        overflowY: "scroll",
        overflowX: "clip",
      }}
    >
      <OuterElementContext.Provider value={other}>
        <AutoSizer>
          {({ width, height }) => (
            <List
              ref={listRef}
              height={height}
              width={width}
              outerElementType={OuterElementType}
              overscanCount={10}
              itemCount={itemCount}
              itemSize={getSize}
              itemData={itemData}
              key={"row"}
              estimatedItemSize={80}
            >
              {({ data, index, style }) => (
                <Row
                  data={data}
                  index={index}
                  style={style}
                  setSize={setSize}
                  windowWidth={windowWidth}
                />
              )}
            </List>
          )}
        </AutoSizer>
      </OuterElementContext.Provider>
    </div>
  );
});

export default ListboxComponent;
